import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useAssessment } from '../../hooks/useAssessment';
import { determineRiskLevel } from '../../utils/euAiAct';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';
const QUESTIONS = [{
  id: 'critical_infrastructure',
  text: 'Is the system used as a safety component in critical infrastructure (e.g., road traffic, water, gas, heating, electricity)?',
  detail: 'Failure could put the life and health of persons at large scale at risk.'
}, {
  id: 'biometric_id',
  text: 'Does the system use real-time or post remote biometric identification of natural persons?',
  detail: 'Includes facial recognition in public spaces.'
}, {
  id: 'employment_decisions',
  text: 'Is the system used for recruitment or selection of natural persons, or for monitoring/evaluating performance?',
  detail: 'Includes CV scanning, automated interview analysis.'
}, {
  id: 'essential_services',
  text: 'Is the system used to evaluate creditworthiness or establish credit scores?',
  detail: 'Excludes detection of financial fraud.'
}, {
  id: 'chatbot',
  text: 'Is the system intended to interact directly with natural persons (e.g., chatbot)?',
  detail: 'Transparency obligations apply.'
}, {
  id: 'emotion_recognition',
  text: 'Is the system used for emotion recognition?',
  detail: 'Requires specific transparency notifications.'
}];
export function RiskClassification() {
  const navigate = useNavigate();
  const {
    setRiskLevel
  } = useAssessment();
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [showResult, setShowResult] = useState(false);
  const handleAnswer = (id: string, value: boolean) => {
    setAnswers(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const calculateResult = () => {
    const level = determineRiskLevel(answers);
    setRiskLevel(level);
    setShowResult(true);
  };
  const riskLevel = determineRiskLevel(answers);
  if (showResult) {
    return <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-631">
        <div className="mb-8" data-id="element-632">
          <h1 className="text-3xl font-bold mb-3" data-id="element-633">Classification Result</h1>
          <p className="text-slate-400 text-lg" data-id="element-634">
            Based on your inputs, the system has been classified under the EU AI
            Act.
          </p>
        </div>

        <Card className="border-l-4 border-l-teal-500" data-id="element-635">
          <div className="flex items-start space-x-6" data-id="element-636">
            <div className="flex-shrink-0" data-id="element-637">
              {riskLevel === 'High' ? <AlertTriangle className="h-12 w-12 text-amber-500" data-id="element-638" /> : <CheckCircle className="h-12 w-12 text-teal-500" data-id="element-639" />}
            </div>
            <div className="flex-1" data-id="element-640">
              <div className="flex items-center justify-between mb-4" data-id="element-641">
                <h2 className="text-2xl font-bold text-white" data-id="element-642">
                  {riskLevel} Risk AI System
                </h2>
                <Badge variant={riskLevel === 'High' ? 'amber' : 'teal'} className="text-sm px-3 py-1" data-id="element-643">
                  {riskLevel} Risk
                </Badge>
              </div>

              <p className="text-slate-300 mb-6 text-lg leading-relaxed" data-id="element-644">
                {riskLevel === 'High' ? 'This system falls under the High-Risk category of the EU AI Act. You must comply with comprehensive obligations regarding data governance, documentation, transparency, human oversight, and robustness.' : riskLevel === 'Limited' ? 'This system presents Limited Risk. The primary obligation is transparency—users must be informed they are interacting with an AI system.' : 'This system presents Minimal Risk. No specific obligations apply under the EU AI Act, though voluntary codes of conduct are encouraged.'}
              </p>

              <div className="bg-navy-900/50 rounded-lg p-4 border border-slate-700 mb-6" data-id="element-645">
                <h4 className="font-semibold text-white mb-2" data-id="element-646">Next Steps</h4>
                <ul className="list-disc list-inside text-slate-400 space-y-1" data-id="element-647">
                  {riskLevel === 'High' && <>
                      <li data-id="element-648">Establish Risk Management System</li>
                      <li data-id="element-649">Verify Data Governance</li>
                      <li data-id="element-650">Compile Technical Documentation</li>
                    </>}
                  {riskLevel === 'Limited' && <li data-id="element-651">Implement Transparency Measures</li>}
                  <li data-id="element-652">Upload Evidence of Compliance</li>
                </ul>
              </div>

              <div className="flex justify-end" data-id="element-653">
                <Button onClick={() => navigate('/assessment/evidence')} size="lg" data-id="element-654">
                  Proceed to Evidence Collection
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>;
  }
  return <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-655">
      <div className="mb-8" data-id="element-656">
        <h1 className="text-3xl font-bold mb-3" data-id="element-657">Risk Classification</h1>
        <p className="text-slate-400 text-lg" data-id="element-658">
          Answer the following questions to determine the regulatory risk level
          of your system.
        </p>
      </div>

      <div className="space-y-6" data-id="element-659">
        {QUESTIONS.map(q => <Card key={q.id} className="transition-all hover:border-slate-600" data-id="element-660">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-661">
              <div className="flex-1" data-id="element-662">
                <h3 className="text-lg font-medium text-white mb-2" data-id="element-663">
                  {q.text}
                </h3>
                <p className="text-sm text-slate-400 flex items-start" data-id="element-664">
                  <Info className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" data-id="element-665" />
                  {q.detail}
                </p>
              </div>
              <div className="flex space-x-3 flex-shrink-0" data-id="element-666">
                <button type="button" onClick={() => handleAnswer(q.id, true)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border ${answers[q.id] === true ? 'bg-teal-500/20 border-teal-500 text-teal-400' : 'bg-navy-900 border-slate-700 text-slate-400 hover:border-slate-500'}`} data-id="element-667">
                  Yes
                </button>
                <button type="button" onClick={() => handleAnswer(q.id, false)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border ${answers[q.id] === false ? 'bg-slate-700 border-slate-600 text-white' : 'bg-navy-900 border-slate-700 text-slate-400 hover:border-slate-500'}`} data-id="element-668">
                  No
                </button>
              </div>
            </div>
          </Card>)}

        <div className="pt-6 flex justify-end border-t border-slate-800" data-id="element-669">
          <Button onClick={calculateResult} disabled={Object.keys(answers).length < QUESTIONS.length} size="lg" data-id="element-670">
            Calculate Risk Level
          </Button>
        </div>
      </div>
    </div>;
}